import { calculateDiscountedTotal, calculatePlatformFee, fmt, nnum } from "@/helpers/quoteFunctions";
import { convertCentsToDollars } from "@thdinc/hpf-shared";
import React, { FC } from "react";
type ItemLine = {
    id: string;
    provider?: string | "";
    name?: string | "";
    qty?: number | string | "";
    costPerItem?: number | string | "";
    profitPerItem?: number | string | "";
    pricePerItem?: number | string | "";
    lineTotal?: number | string | "";
};

interface QuoteForm {
    items: ItemLine[];
    platformFee: number;
    discount: {
        enabled: boolean;
        name: string;
        amount: number | string | "";
        percentage: number | string | "";
    };
}

/* ---------- Component ---------- */
const BartenderPreview: FC<QuoteForm> = ({
    items: parentItem,
    discount: parentDiscount,
    platformFee = 0,
}) => {
    const companyProvide = parentItem.filter((i) => i.provider !== "customerProvide");
    const customerProvide = parentItem.filter((i) => i.provider == "customerProvide");
    const subTotalBP = React.useMemo(
        () => parentItem.reduce((a, c) => a + nnum(c.lineTotal), 0),
        [parentItem]
    );
    let discount = 0;
    if (parentDiscount?.enabled) {
        const amount = Number(parentDiscount.amount) || 0;
        const percentage = Number(parentDiscount.percentage) || 0;
        if (amount > 0) discount = amount;
        else if (percentage > 0) discount = (subTotalBP * percentage) / 100;
    }
    const total = subTotalBP
        ? calculateDiscountedTotal(subTotalBP, parentDiscount)
        : 0;
    // const total = subTotalBP
    //     ? calculateDiscountedTotal(subTotalBP, parentDiscount) + calculatePlatformFee(subTotalBP, platformFee)
    //     : 0;

    return (
        <div className="w-full bg-white rounded-2xl shadow-sm">
            {/* Title */}
            <h1 className="font-[600] text-[16px] text-[#282828]">Quote</h1>

            {/* Section header */}
            <h2 className="mt-2 mb-3 text text-[#202224] font-[600] text-[12.62px]">
                Package Details
            </h2>

            {/* Table */}
            <div className="overflow-x-auto">
                {/* Header */}
                <div className="grid grid-cols-4 font-[500] text-xs text-[#202224] border-b border-gray-200 border-t">
                    <div className="px-4 py-3 text-left text-[10px]">Item</div>
                    <div className="px-4 py-3 text-center text-[10px]">Qty</div>
                    <div className="px-4 py-3 text-center text-[10px]">
                        Price per item
                    </div>
                    <div className="px-4 py-3 text-right text-[10px]">Amount</div>
                </div>
                <div className="divide-y text-[#202224] border-b border-gray-200">
                    {companyProvide.map((it, idx) => (
                        <div
                            key={idx}
                            className="grid grid-cols-4 text-[10px] text-[#202224]"
                        >
                            <div className="px-4 py-3 max-w-full truncate ">{it.name}</div>
                            <div className="px-4 py-3 text-center ">
                                {typeof it.qty === "number" ? it.qty : "-"}
                            </div>
                            <div className="px-4 py-3 text-center ">
                                {typeof it.pricePerItem === "number"
                                    ? `$${convertCentsToDollars(
                                        (it.pricePerItem * 100).toString()
                                    )}`
                                    : "-"}
                            </div>
                            <div className="px-4 py-3 text-right">
                                {typeof it.lineTotal === "number"
                                    ? `$${convertCentsToDollars((it.lineTotal * 100).toString())}`
                                    : "-"}
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Summary */}
            <div className="mt-6 grid grid-cols-2 gap-4  w-full">
                <div className="w-full">{/* LEFT COLUMN */}</div>

                <div className="text-sm w-full">
                    {/* RIGHT COLUMN */}
                    <Row label="Subtotal" value={fmt(subTotalBP)} />
                    {discount > 0 && <Row label="Discount" value={fmt(discount)} />}
                    {/* <Row label="HOT Platform Fee" value={fmt(calculatePlatformFee(subTotalBP, platformFee))} /> */}
                    {/* <p className="text-[9px] text-[#8D8D8D] font-[400] ">
                        (All platform fees are non-refundable)
                    </p> */}
                    <div className="mt-3 flex items-center justify-between border-t pt-3 border-gray-200">
                        <span className="text-sm font-m
            um text-gray-700">Total</span>
                        <span className="text-lg font-semibold text-gray-900">
                            {fmt(total)}
                        </span>
                    </div>
                </div>
            </div>

            {/* Customer Provides - only when there is client-provided data */}
            {customerProvide.length > 0 && (
                <div className="mt-6 rounded-2xl bg-gray-50 p-4">
                    <p className="text-sm text-gray-800 mb-3 text-[10px] font-[600]">
                        Client Provides
                    </p>
                    <div className="flex flex-wrap gap-2">
                        {customerProvide.map((chip, i) => (
                            <span
                                key={i}
                                className="inline-flex items-center gap-2 max-w-[120px] overflow-hidden rounded-full bg-white px-3 py-1.5 text-sm"
                            >
                                <span className="text-[10px] truncate block min-w-0">{chip?.name}</span>
                                {/* <span className="inline-flex h-6 items-center px-2 text-[10px] text-[#8D8D8D] ">
                                {Number(chip.qty) > 0 && chip.qty}
                            </span> */}
                            </span>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default BartenderPreview;

/* ---------- Small subcomponent ---------- */
function Row({ label, value }: { label: string; value: string }) {
    return (
        <div className="flex items-center justify-between text-[12px] font-[400]">
            <span className="text-gray-600">{label}</span>
            <span className="text-gray-800">{value}</span>
        </div>
    );
}
